import { inject } from '@angular/core';
import { CanMatchFn, Routes } from '@angular/router';
import { pageDataGuard } from './core/page-data.service';
import { AuthPage } from './pages/auth.page';
import { AccountPage } from './pages/account.page';
import { SecurityCenterPage } from './pages/security-center.page';
import { AccountRecoveryPage } from './pages/account-recovery.page';
import { CollectionPage } from './pages/collection.page';
import { BlogDetailPage } from './pages/blog-detail.page';
import { BlogPage } from './pages/blog.page';
import { CreditTopUpPage } from './pages/credit-top-up.page';
import { FaqPage } from './pages/faq.page';
import { FeedsPage } from './pages/feeds.page';
import { FortunePage } from './pages/fortune.page';
import { HomePage } from './pages/home.page';
import { LegalPage } from './pages/legal.page';
import { ListingPage } from './pages/listing.page';
import { LivePage } from './pages/live.page';
import { NotificationsPage } from './pages/notifications.page';
import { SellerHistoryPage } from './pages/seller-history.page';
import { SellerOnboardingPage } from './pages/seller-onboarding.page';
import { SellersPage } from './pages/sellers.page';
import { SupportPage } from './pages/support.page';
import { SupportTicketsPage } from './pages/support-tickets.page';
import { BrandSectionPage } from './pages/brand-section.page';
import { TeamDetailPage } from './pages/team-detail.page';
import { NotFoundPage } from './pages/not-found.page';
import { ServiceDetailPage } from './pages/service-detail.page';
import { AymudoManagedServicePage } from './pages/aymudo-managed-service.page';
import { AvailableCallSellersPage } from './pages/available-call-sellers.page';
import { CartPage } from './pages/cart.page';
import { CheckoutPage } from './pages/checkout.page';
import { WalletTopUpCheckoutPage } from './pages/wallet-top-up-checkout.page';
import { GuestOrderPage } from './pages/guest-order.page';
import { PaymentLinkPage } from './pages/payment-link.page';
import { siteRouteGuard, supportedLocaleRouteGuard } from './core/site-route.guard';
import { AymudoSolutionPage } from './pages/aymudo-solution.page';
import { AymudoQuotePage } from './pages/aymudo-quote.page';
import { AymudoSolutionsPage } from './pages/aymudo-solutions.page';
import { AymudoProductLandingPage } from './pages/aymudo-product-landing.page';
import { AymudoMarketingHubPage } from './pages/aymudo-marketing-hub.page';
import { AymudoProductsEntryPage } from './pages/aymudo-products-entry.page';
import { AymudoCapabilityPage } from './pages/aymudo-capability.page';
import { AymudoResourcesPage } from './pages/aymudo-resources.page';
import { AymudoSubscribePage } from './pages/aymudo-subscribe.page';
import { GoogleCallbackPage } from './pages/google-callback.page';
import { FacebookSocialCallbackPage } from './pages/facebook-social-callback.page';
import { GoogleBusinessCallbackPage } from './pages/google-business-callback.page';
import { MicrosoftCallbackPage } from './pages/microsoft-callback.page';
import { FreeFortunePage } from './pages/free-fortune.page';
import { SellerFreeFortunesPage } from './pages/seller-free-fortunes.page';
import { SellerTemplateEditPage } from './pages/seller-template-edit.page';
import { ForumV2Page } from './pages/forum-v2.page';
import { ForumIndexPage } from './pages/forum-index.page';
import { ForumTopicPage } from './pages/forum-topic.page';
import { ForumDirectoryPage } from './pages/forum-directory.page';
import { ForumThreadPage } from './pages/forum-thread.page';
import { SearchPage } from './pages/search.page';
import { AboutPage } from './pages/about.page';
import { SiteConfigService } from './core/site-config.service';

const loadSellerDashboard = () =>
  import('./pages/seller-dashboard.page').then((module) => module.SellerDashboardPage);
const loadQrMarketplaceDashboard = () =>
  import('./pages/qr-marketplace-dashboard.page').then(
    (module) => module.QrMarketplaceDashboardPage,
  );
const loadSellerFreeFortuneDetail = () =>
  import('./pages/seller-free-fortune-detail.page').then(
    (module) => module.SellerFreeFortuneDetailPage,
  );
const loadAdminPanel = () =>
  import('./pages/admin-panel.page').then((module) => module.AdminPanelPage);
const loadThemeEditor = () =>
  import('./pages/theme-editor.page').then((module) => module.ThemeEditorPage);
const loadAppointmentBusinessDirectory = () =>
  import('./pages/appointment-business-directory.page').then(
    (module) => module.AppointmentBusinessDirectoryPage,
  );
const loadAppointmentBusinessDetail = () =>
  import('./pages/appointment-business-detail.page').then(
    (module) => module.AppointmentBusinessDetailPage,
  );
const loadAppointmentBooking = () =>
  import('./pages/appointment-booking.page').then((module) => module.AppointmentBookingPage);
const loadCustomerAppointments = () =>
  import('./pages/customer-appointments.page').then((module) => module.CustomerAppointmentsPage);
const loadAppointmentBranchBooking = () =>
  import('./pages/appointment-branch-booking.page').then(
    (module) => module.AppointmentBranchBookingPage,
  );
const loadAppointmentPlans = () =>
  import('./pages/appointment-plans.page').then((module) => module.AppointmentPlansPage);
const loadOperationalWorkspace = () =>
  import('./pages/operational-workspace.page').then((module) => module.OperationalWorkspacePage);
const loadAccountSubscriptions = () =>
  import('./pages/account-subscriptions.page').then((module) => module.AccountSubscriptionsPage);
const loadAymudoAiDesktop = () =>
  import('./pages/aymudo-ai-desktop.page').then((module) => module.AymudoAiDesktopPage);
const loadAymoCustomerService = () =>
  import('./pages/aymo-customer-service.page').then(
    (module) => module.AymoCustomerServicePage,
  );
const loadCustomerServiceRepresentative = () =>
  import('./pages/customer-service-representative.page').then(
    (module) => module.CustomerServiceRepresentativePage,
  );
const loadQrBranchMenu = () =>
  import('./pages/qr-branch-menu.page').then((module) => module.QrBranchMenuPage);
const loadStaffInvitation = () =>
  import('./pages/staff-invitation.page').then((module) => module.StaffInvitationPage);
const qrMarketplaceBranchRouteGuard: CanMatchFn = async () => {
  if (typeof window === 'undefined') {
    const bootstrap = (
      globalThis as typeof globalThis & { __AYMUDO_PAGE_DATA__?: Record<string, any> }
    ).__AYMUDO_PAGE_DATA__;
    return bootstrap?.['meta']?.['siteConfig']?.['businessMode'] === 'qrMarketplace';
  }
  const site = inject(SiteConfigService);
  await site.ready();
  return site.config().businessMode === 'qrMarketplace';
};
const appointmentMarketplaceBranchRouteGuard: CanMatchFn = async () => {
  if (typeof window === 'undefined') {
    const bootstrap = (
      globalThis as typeof globalThis & { __AYMUDO_PAGE_DATA__?: Record<string, any> }
    ).__AYMUDO_PAGE_DATA__;
    return ['appointmentMarketplace', 'veterinaryMarketplace'].includes(
      String(bootstrap?.['meta']?.['siteConfig']?.['businessMode'] || ''),
    );
  }
  const site = inject(SiteConfigService);
  await site.ready();
  return ['appointmentMarketplace', 'veterinaryMarketplace'].includes(
    String(site.config().businessMode || ''),
  );
};
const directClinicMarketplaceRouteGuard: CanMatchFn = async () => {
  if (typeof window === 'undefined') {
    const bootstrap = (
      globalThis as typeof globalThis & { __AYMUDO_PAGE_DATA__?: Record<string, any> }
    ).__AYMUDO_PAGE_DATA__;
    const config = bootstrap?.['meta']?.['siteConfig'];
    return config?.['brandKey'] !== 'aymudo' && (
      config?.['businessMode'] === 'veterinaryMarketplace' || config?.['brandKey'] === 'klinik'
    );
  }
  const site = inject(SiteConfigService);
  await site.ready();
  return site.config().brandKey !== 'aymudo' && (
    site.config().businessMode === 'veterinaryMarketplace' || site.config().brandKey === 'klinik'
  );
};
const aymudoMarketingRouteGuard: CanMatchFn = async () => {
  if (typeof window === 'undefined') {
    const bootstrap=(globalThis as typeof globalThis & {__AYMUDO_PAGE_DATA__?:Record<string,any>}).__AYMUDO_PAGE_DATA__;
    return bootstrap?.['meta']?.['siteConfig']?.['brandKey']==='aymudo';
  }
  const site=inject(SiteConfigService);await site.ready();return site.config().brandKey==='aymudo';
};

const pageRoutes: Routes = [
  { path: 'google-giris', component: GoogleCallbackPage },
  { path: 'personel-daveti/:code', loadComponent: loadStaffInvitation },
  { path: '', component: HomePage },
  { path: 'aymo', loadComponent: loadAymoCustomerService },
  { path: 'customer-service/application', loadComponent: loadCustomerServiceRepresentative, data: { application: true } },
  { path: 'customer-service/become-representative', loadComponent: loadCustomerServiceRepresentative },
  { path: 'customer-service', loadComponent: () => import('./pages/customer-service-console.page').then(m => m.CustomerServiceConsolePage) },
  { path: 'musteri-hizmetleri-temsilcisi/basvuru', loadComponent: loadCustomerServiceRepresentative, data: { application: true } },
  { path: 'musteri-hizmetleri-temsilcisi', loadComponent: loadCustomerServiceRepresentative },
  { path: 'gpt-astra', loadComponent: loadAymudoAiDesktop },
  { path: 'hizmetler', canMatch:[aymudoMarketingRouteGuard], component:AymudoManagedServicePage },
  {
    path: 'hizmetler',
    canMatch: [directClinicMarketplaceRouteGuard],
    redirectTo: '',
    pathMatch: 'full',
  },
  {
    path: 'klinikler',
    canMatch: [directClinicMarketplaceRouteGuard],
    redirectTo: '',
    pathMatch: 'full',
  },
  {
    path: 'isletmeler',
    canMatch: [directClinicMarketplaceRouteGuard],
    redirectTo: '',
    pathMatch: 'full',
  },
  {
    path: 'subeler',
    canMatch: [directClinicMarketplaceRouteGuard],
    redirectTo: '',
    pathMatch: 'full',
  },
  { path: 'neden-aymudo', component: AymudoMarketingHubPage, data: { hubKind: 'why' } },
  { path: 'why-aymudo', component: AymudoMarketingHubPage, data: { hubKind: 'why' } },
  { path: 'fiyatlandirma', component: AymudoMarketingHubPage, data: { hubKind: 'pricing' } },
  { path: 'pricing', component: AymudoMarketingHubPage, data: { hubKind: 'pricing' } },
  { path: 'urunler', component: AymudoProductsEntryPage, data: { hubKind: 'products' } },
  { path: 'urunler/:slug', component: AymudoCapabilityPage },
  { path: 'hizmetler/:slug', canMatch:[aymudoMarketingRouteGuard], component: AymudoManagedServicePage },
  { path: 'products', component: AymudoProductsEntryPage, data: { hubKind: 'products' } },
  { path: 'products/:slug', component: AymudoCapabilityPage },
  { path: 'abone-ol', component: AymudoSubscribePage },
  { path: 'subscribe', component: AymudoSubscribePage },
  { path: 'aboneliklerim', loadComponent: loadAccountSubscriptions },
  { path: 'subscriptions', loadComponent: loadAccountSubscriptions },
  { path: 'kaynaklar', component: AymudoResourcesPage },
  { path: 'resources', component: AymudoResourcesPage },
  { path: 'cozumler', component: AymudoSolutionsPage },
  { path: 'cozumler/qr-menu', component: AymudoProductLandingPage, data: { productKey: 'qrMenu' } },
  { path: 'cozumler/:slug', component: AymudoSolutionPage },
  { path: 'teklif/:token', component: AymudoQuotePage },
  { path: 'solutions', component: AymudoSolutionsPage },
  {
    path: 'solutions/qr-menu',
    component: AymudoProductLandingPage,
    data: { productKey: 'qrMenu' },
  },
  { path: 'solutions/:slug', component: AymudoSolutionPage },
  { path: 'loesungen', component: AymudoSolutionsPage },
  {
    path: 'loesungen/qr-menu',
    component: AymudoProductLandingPage,
    data: { productKey: 'qrMenu' },
  },
  { path: 'loesungen/:slug', component: AymudoSolutionPage },
  { path: 'soluciones', component: AymudoSolutionsPage },
  {
    path: 'soluciones/qr-menu',
    component: AymudoProductLandingPage,
    data: { productKey: 'qrMenu' },
  },
  { path: 'soluciones/:slug', component: AymudoSolutionPage },
  { path: 'themes', component: AymudoProductLandingPage, data: { productKey: 'themes' } },
  { path: 'commerce', component: AymudoProductLandingPage, data: { productKey: 'commerce' } },
  {
    path: 'appointments',
    component: AymudoProductLandingPage,
    data: { productKey: 'appointments' },
  },
  { path: 'social', component: AymudoProductLandingPage, data: { productKey: 'social' } },
  { path: 'qr-menu', component: AymudoProductLandingPage, data: { productKey: 'qrMenu' } },
  { path: 'blog', component: BlogPage },
  { path: 'blog/:slug', component: BlogDetailPage },
  { path: 'about', component: AboutPage },
  { path: 'arama', component: SearchPage },
  { path: 'search', component: SearchPage },
  { path: 'suche', component: SearchPage },
  { path: 'buscar', component: SearchPage },
  { path: 'recherche', component: SearchPage },
  { path: 'collection/:id/:slug', component: CollectionPage },
  { path: 'koleksiyon/:id/:slug', component: CollectionPage },
  { path: 'akis', component: FeedsPage },
  { path: 'akis/:id', component: FeedsPage },
  { path: 'fal', component: FortunePage },
  { path: 'fal-baktir', component: FortunePage },
  { path: 'fal-baktir/:slug', component: FortunePage },
  { path: 'falci-sec', component: FortunePage, data: { fortuneStep: 'seller' } },
  { path: 'fali-gonder', component: FortunePage, data: { fortuneStep: 'submit' } },
  { path: 'ucretsiz-fal', component: FreeFortunePage },
  { path: 'ucretsiz-fal/:id', loadComponent: loadSellerFreeFortuneDetail },
  { path: 'ucretsiz-fallar', component: FreeFortunePage },
  { path: 'free-fortune', component: FreeFortunePage },
  { path: 'free-coffee-reading', component: FreeFortunePage },
  { path: 'free-fortunes', component: FreeFortunePage },
  { path: 'urunler', component: AymudoProductsEntryPage, data: { hubKind: 'products' } },
  { path: 'urunler/:slug', component: AymudoCapabilityPage },
  { path: 'isletmeler', loadComponent: loadAppointmentBusinessDirectory },
  { path: 'isletme/:slug', loadComponent: loadAppointmentBusinessDetail },
  { path: 'paketler', loadComponent: loadAppointmentPlans },
  { path: 'hizmetler', canMatch:[aymudoMarketingRouteGuard], component: AymudoManagedServicePage },
  { path: 'hizmetler', component: BrandSectionPage },
  { path: 'hizmet/:slug', component: ServiceDetailPage },
  { path: 'ekibimiz', component: BrandSectionPage },
  { path: 'ekibimiz/:slug', component: TeamDetailPage },
  { path: 'subeler', component: BrandSectionPage },
  { path: 'fiyat-listesi', component: BrandSectionPage },
  { path: 'randevu', loadComponent: loadAppointmentBooking },
  { path: 'randevum/:code', loadComponent: loadCustomerAppointments },
  { path: 'randevularim', loadComponent: loadCustomerAppointments },
  { path: 'kategori/:slug', component: FortunePage },
  { path: 'falcilar', component: SellersPage },
  { path: 'forum/topic/:slug', component: ForumThreadPage },
  { path: 'forum/category/:categorySlug', component: ForumDirectoryPage },
  { path: 'forum', component: ForumDirectoryPage },
  { path: 'musait-falcilar', component: AvailableCallSellersPage },
  { path: 'falci-ol', component: SellerOnboardingPage },
  { path: 'seller-panel/free-fortunes', component: SellerFreeFortunesPage },
  { path: 'seller-panel/free-fortunes/:id', loadComponent: loadSellerFreeFortuneDetail },
  { path: 'seller-panel/ucretsiz-fallar', component: SellerFreeFortunesPage },
  { path: 'seller-panel/ucretsiz-fallar/:id', loadComponent: loadSellerFreeFortuneDetail },
  { path: 'seller-panel/orders/:id', loadComponent: () => import('./pages/seller-order-detail.page').then(m => m.SellerOrderDetailPage) },
  { path: 'seller-panel/siparisler/:id', loadComponent: () => import('./pages/seller-order-detail.page').then(m => m.SellerOrderDetailPage) },
  { path: 'fallarim', loadComponent: () => import('./pages/orders.page').then(m => m.OrdersPage) },
  { path: 'fallarim/:slug', loadComponent: () => import('./pages/orders.page').then(m => m.OrdersPage) },
  { path: 'siparislerim', loadComponent: () => import('./pages/orders.page').then(m => m.OrdersPage) },
  { path: 'siparislerim/:slug', loadComponent: () => import('./pages/orders.page').then(m => m.OrdersPage) },
  { path: 'seller-panel/hazir-sablonlar/:id', component: SellerTemplateEditPage },
  {
    path: 'seller-panel/hazir-sablonlar',
    loadComponent: loadSellerDashboard,
    data: { quickTemplateList: true },
  },
  { path: 'seller-panel/product-templates/:id', component: SellerTemplateEditPage },
  {
    path: 'seller-panel/product-templates',
    loadComponent: loadSellerDashboard,
    data: { quickTemplateList: true },
  },
  { path: 'admin-panel/sellers/free-fortune', loadComponent: () => import('./pages/admin-free-fortune.page').then(m => m.AdminFreeFortunePage) },
  { path: 'admin-panel/system/theme', loadComponent: loadThemeEditor },
  { path: 'admin-panel/system/tema', loadComponent: loadThemeEditor },
  { path: 'admin-panel', loadComponent: loadAdminPanel },
  { path: 'admin-panel/:section/:action/:subaction/:id', loadComponent: loadAdminPanel },
  { path: 'admin-panel/:section/:action/:id', loadComponent: loadAdminPanel },
  { path: 'admin-panel/:section/:action', loadComponent: loadAdminPanel },
  { path: 'admin-panel/:section', loadComponent: loadAdminPanel },
  { path: 'fal/:slug', component: ListingPage },
  { path: 'falci/:slug', loadComponent: () => import('./pages/seller.page').then(m => m.SellerPage) },
  { path: 'falci/:slug/gecmis-fallar', component: SellerHistoryPage },
  { path: 'urun/:slug', component: ListingPage },
  { path: 'payment/:code', component: PaymentLinkPage },
  { path: 'sepet', component: CartPage },
  { path: 'cart', component: CartPage },
  { path: 'odeme', component: CheckoutPage },
  { path: 'checkout', component: CheckoutPage },
  { path: 'odeme/:id', component: CheckoutPage },
  { path: 'checkout/:id', component: CheckoutPage },
  { path: 'misafir-siparis/:id', component: GuestOrderPage },
  { path: 'misafir-siparis/:id/:token', component: GuestOrderPage },
  { path: 'misafir-siparisi/:id', component: GuestOrderPage },
  { path: 'misafir-siparisi/:id/:token', component: GuestOrderPage },
  { path: 'saticilar', component: SellersPage },
  { path: 'satici/:slug', loadComponent: () => import('./pages/seller.page').then(m => m.SellerPage) },
  { path: 'satici-ol', component: SellerOnboardingPage },
  { path: 'siparislerim', loadComponent: () => import('./pages/orders.page').then(m => m.OrdersPage) },
  { path: 'siparislerim/:slug', loadComponent: () => import('./pages/orders.page').then(m => m.OrdersPage) },
  { path: 'kredi-al', component: CreditTopUpPage },
  { path: 'kredi-al/odeme/:id', component: WalletTopUpCheckoutPage },
  { path: 'credit-checkout/:id', component: WalletTopUpCheckoutPage },
  { path: 'wallet-checkout/:id', component: WalletTopUpCheckoutPage },
  { path: 'walletCheckout/:id', component: WalletTopUpCheckoutPage },
  { path: 'wallet-top-up/:id', component: WalletTopUpCheckoutPage },
  { path: 'walletTopUp/:id', component: WalletTopUpCheckoutPage },
  { path: 'profil', component: AccountPage },
  { path: 'profil/guvenlik', component: SecurityCenterPage },
  { path: 'hesap-kurtarma', component: AccountRecoveryPage },
  { path: 'iletisim-guncelle', component: AccountPage },
  { path: 'sifre-guncelle', component: AccountPage },
  { path: 'yorumlarim', component: AccountPage },
  { path: 'son-gezdiklerim', component: AccountPage },
  { path: 'takip-ettiklerim', component: AccountPage },
  { path: 'koleksiyonlarim', component: AccountPage },
  { path: 'aramalarim', component: AccountPage },
  { path: 'bildirimler', component: NotificationsPage },
  { path: 'fallarim', loadComponent: () => import('./pages/orders.page').then(m => m.OrdersPage) },
  { path: 'siparislerim', loadComponent: () => import('./pages/orders.page').then(m => m.OrdersPage) },
  { path: 'fallarim/:slug', loadComponent: () => import('./pages/orders.page').then(m => m.OrdersPage) },
  { path: 'siparislerim/:slug', loadComponent: () => import('./pages/orders.page').then(m => m.OrdersPage) },
  { path: 'sss', component: FaqPage },
  { path: 'sss/:slug', component: FaqPage },
  { path: 'faq', component: FaqPage },
  { path: 'faq/:slug', component: FaqPage },
  { path: 'yasal', component: LegalPage },
  { path: 'yasal/:slug', component: LegalPage },
  { path: 'legal', component: LegalPage },
  { path: 'legal/:slug', component: LegalPage },
  { path: 'destek', component: SupportPage },
  { path: 'support', component: SupportPage },
  { path: 'hilfe', component: SupportPage },
  { path: 'soporte', component: SupportPage },
  { path: 'destek/taleplerim', component: SupportTicketsPage },
  { path: 'destek/taleplerim/:slug', component: SupportTicketsPage },
  { path: 'destek/:slug', component: SupportPage },
  { path: 'canli/:id', component: LivePage },
  { path: 'giris-yap', component: AuthPage },
  { path: 'kayit-ol', component: AuthPage },
  { path: 'sifremi-unuttum', component: AuthPage },
  { path: 'login', component: AuthPage },
  { path: 'register', component: AuthPage },
  { path: 'forgot-password', component: AuthPage },
];

const localizedPageRoutes: Routes = [
  { path: '', component: HomePage },
  { path: 'about', component: AboutPage },
  { path: 'products', component: AymudoProductsEntryPage },
  { path: 'produkte', component: AymudoProductsEntryPage },
  { path: 'productos', component: AymudoProductsEntryPage },
  { path: 'produits', component: AymudoProductsEntryPage },
  {
    path: 'services',
    canMatch: [directClinicMarketplaceRouteGuard],
    redirectTo: '',
    pathMatch: 'full',
  },
  {
    path: 'clinics',
    canMatch: [directClinicMarketplaceRouteGuard],
    redirectTo: '',
    pathMatch: 'full',
  },
  { path: 'themes', component: AymudoProductLandingPage, data: { productKey: 'themes' } },
  { path: 'commerce', component: AymudoProductLandingPage, data: { productKey: 'commerce' } },
  {
    path: 'appointments',
    component: AymudoProductLandingPage,
    data: { productKey: 'appointments' },
  },
  { path: 'social', component: AymudoProductLandingPage, data: { productKey: 'social' } },
  { path: 'qr-menu', component: AymudoProductLandingPage, data: { productKey: 'qrMenu' } },
  { path: 'app/:widget', loadComponent: loadOperationalWorkspace },
  { path: 'app', loadComponent: loadOperationalWorkspace },
  { path: 'subscriptions', loadComponent: loadAccountSubscriptions },
  { path: 'abonnements', loadComponent: loadAccountSubscriptions },
  { path: 'suscripciones', loadComponent: loadAccountSubscriptions },
  { path: 'yonetim', loadComponent: loadQrMarketplaceDashboard },
  { path: 'services', component: BrandSectionPage },
  { path: 'service/:slug', component: ServiceDetailPage },
  { path: 'team', component: BrandSectionPage },
  { path: 'team/:slug', component: TeamDetailPage },
  { path: 'clinics', component: BrandSectionPage },
  { path: 'fees', component: BrandSectionPage },
  { path: 'appointment', loadComponent: loadAppointmentBooking },
  { path: 'randevu', loadComponent: loadAppointmentBooking },
  { path: 'appointment/:code/manage', loadComponent: loadCustomerAppointments },
  { path: 'my-appointments', loadComponent: loadCustomerAppointments },
  { path: 'randevum/:code', loadComponent: loadCustomerAppointments },
  { path: 'randevularim', loadComponent: loadCustomerAppointments },
  { path: 'cart', component: CartPage },
  { path: 'checkout', component: CheckoutPage },
  { path: 'checkout/:id', component: CheckoutPage },
  { path: 'guest-order/:id', component: GuestOrderPage },
  { path: 'guest-order/:id/:token', component: GuestOrderPage },
  { path: 'gastbestellung/:id', component: GuestOrderPage },
  { path: 'gastbestellung/:id/:token', component: GuestOrderPage },
  { path: 'pedido-invitado/:id', component: GuestOrderPage },
  { path: 'pedido-invitado/:id/:token', component: GuestOrderPage },
  { path: 'commande-invite/:id', component: GuestOrderPage },
  { path: 'commande-invite/:id/:token', component: GuestOrderPage },
  { path: 'fortune', component: FortunePage },
  { path: 'choose-reader', component: FortunePage, data: { fortuneStep: 'seller' } },
  { path: 'send-fortune', component: FortunePage, data: { fortuneStep: 'submit' } },
  { path: 'free-fortune', component: FreeFortunePage },
  { path: 'free-coffee-reading', component: FreeFortunePage },
  { path: 'kostenloses-kaffeeorakel', component: FreeFortunePage },
  { path: 'lectura-de-cafe-gratis', component: FreeFortunePage },
  { path: 'voyance-cafe-gratuite', component: FreeFortunePage },
  { path: 'wahrsagen', component: FortunePage },
  { path: 'berater-waehlen', component: FortunePage, data: { fortuneStep: 'seller' } },
  { path: 'wahrsagung-senden', component: FortunePage, data: { fortuneStep: 'submit' } },
  { path: 'videncia', component: FortunePage },
  { path: 'elegir-vidente', component: FortunePage, data: { fortuneStep: 'seller' } },
  { path: 'enviar-videncia', component: FortunePage, data: { fortuneStep: 'submit' } },
  { path: 'voyance', component: FortunePage },
  { path: 'choisir-voyant', component: FortunePage, data: { fortuneStep: 'seller' } },
  { path: 'envoyer-voyance', component: FortunePage, data: { fortuneStep: 'submit' } },
  { path: 'category/:slug', component: FortunePage },
  { path: 'kategorie/:slug', component: FortunePage },
  { path: 'categoria/:slug', component: FortunePage },
  { path: 'categorie/:slug', component: FortunePage },
  { path: 'readers', component: SellersPage },
  { path: 'available-readers', component: AvailableCallSellersPage },
  { path: 'verfuegbare-berater', component: AvailableCallSellersPage },
  { path: 'videntes-disponibles', component: AvailableCallSellersPage },
  { path: 'voyants-disponibles', component: AvailableCallSellersPage },
  { path: 'become-reader', component: SellerOnboardingPage },
  { path: 'seller-panel', loadComponent: loadSellerDashboard },
  { path: 'seller-panel/orders/:id', loadComponent: () => import('./pages/seller-order-detail.page').then(m => m.SellerOrderDetailPage) },
  { path: 'seller-panel/siparisler/:id', loadComponent: () => import('./pages/seller-order-detail.page').then(m => m.SellerOrderDetailPage) },
  { path: 'seller-panel/hazir-sablonlar/:id', component: SellerTemplateEditPage },
  {
    path: 'seller-panel/hazir-sablonlar',
    loadComponent: loadSellerDashboard,
    data: { quickTemplateList: true },
  },
  { path: 'seller-panel/product-templates/:id', component: SellerTemplateEditPage },
  {
    path: 'seller-panel/product-templates',
    loadComponent: loadSellerDashboard,
    data: { quickTemplateList: true },
  },
  { path: 'seller-panel/:section/:action/:id', loadComponent: loadSellerDashboard },
  { path: 'seller-panel/:section/:action', loadComponent: loadSellerDashboard },
  { path: 'seller-panel/:section', loadComponent: loadSellerDashboard },
  {
    path: 'admin-panel/urun-sablonlari/yeni',
    loadComponent: loadSellerDashboard,
    data: { productTemplateMode: true },
  },
  {
    path: 'admin-panel/product-templates/new',
    loadComponent: loadSellerDashboard,
    data: { productTemplateMode: true },
  },
  { path: 'admin-panel/system/theme', loadComponent: loadThemeEditor },
  { path: 'admin-panel/system/tema', loadComponent: loadThemeEditor },
  { path: 'admin-panel', loadComponent: loadAdminPanel },
  { path: 'admin-panel/:section/:action/:id', loadComponent: loadAdminPanel },
  { path: 'admin-panel/:section/:action', loadComponent: loadAdminPanel },
  { path: 'admin-panel/:section', loadComponent: loadAdminPanel },
  { path: 'berater', component: SellersPage },
  { path: 'berater-werden', component: SellerOnboardingPage },
  { path: 'videntes', component: SellersPage },
  { path: 'ser-vidente', component: SellerOnboardingPage },
  { path: 'voyants', component: SellersPage },
  { path: 'devenir-voyant', component: SellerOnboardingPage },
  { path: 'fortune/:slug', component: ListingPage },
  { path: 'product/:slug', component: ListingPage },
  { path: 'produkt/:slug', component: ListingPage },
  { path: 'producto/:slug', component: ListingPage },
  { path: 'produit/:slug', component: ListingPage },
  { path: 'wahrsagen/:slug', component: ListingPage },
  { path: 'videncia/:slug', component: ListingPage },
  { path: 'voyance/:slug', component: ListingPage },
  { path: 'reader/:slug', loadComponent: () => import('./pages/seller.page').then(m => m.SellerPage) },
  { path: 'berater/:slug', loadComponent: () => import('./pages/seller.page').then(m => m.SellerPage) },
  { path: 'vidente/:slug', loadComponent: () => import('./pages/seller.page').then(m => m.SellerPage) },
  { path: 'voyant/:slug', loadComponent: () => import('./pages/seller.page').then(m => m.SellerPage) },
  { path: 'admin-panel', loadComponent: loadAdminPanel },
  { path: 'admin-panel/:section/:action/:id', loadComponent: loadAdminPanel },
  { path: 'admin-panel/:section/:action', loadComponent: loadAdminPanel },
  { path: 'admin-panel/:section', loadComponent: loadAdminPanel },
  { path: 'credits', component: CreditTopUpPage },
  { path: 'credits/checkout/:id', component: WalletTopUpCheckoutPage },
  { path: 'credits/odeme/:id', component: WalletTopUpCheckoutPage },
  { path: 'credits/paiement/:id', component: WalletTopUpCheckoutPage },
  { path: 'guthaben', component: CreditTopUpPage },
  { path: 'guthaben/zahlung/:id', component: WalletTopUpCheckoutPage },
  { path: 'guthaben/odeme/:id', component: WalletTopUpCheckoutPage },
  { path: 'creditos', component: CreditTopUpPage },
  { path: 'creditos/pago/:id', component: WalletTopUpCheckoutPage },
  { path: 'creditos/odeme/:id', component: WalletTopUpCheckoutPage },
  { path: 'profile', component: AccountPage },
  { path: 'profile/security', component: SecurityCenterPage },
  { path: 'account-recovery', component: AccountRecoveryPage },
  { path: 'contact', component: AccountPage },
  { path: 'password', component: AccountPage },
  { path: 'reviews', component: AccountPage },
  { path: 'history', component: AccountPage },
  { path: 'following', component: AccountPage },
  { path: 'collections', component: AccountPage },
  { path: 'calls', component: AccountPage },
  { path: 'notifications', component: NotificationsPage },
  { path: 'my-readings', loadComponent: () => import('./pages/orders.page').then(m => m.OrdersPage) },
  { path: 'my-readings/:slug', loadComponent: () => import('./pages/orders.page').then(m => m.OrdersPage) },
  { path: 'orders', loadComponent: () => import('./pages/orders.page').then(m => m.OrdersPage) },
  { path: 'orders/:slug', loadComponent: () => import('./pages/orders.page').then(m => m.OrdersPage) },
  { path: 'support', component: SupportPage },
  { path: 'support/:slug', component: SupportPage },
  { path: 'hilfe', component: SupportPage },
  { path: 'hilfe/:slug', component: SupportPage },
  { path: 'soporte', component: SupportPage },
  { path: 'soporte/:slug', component: SupportPage },
  { path: 'support/tickets', component: SupportTicketsPage },
  { path: 'hilfe/tickets', component: SupportTicketsPage },
  { path: 'soporte/tickets', component: SupportTicketsPage },
  { path: 'support/tickets/:slug', component: SupportTicketsPage },
  { path: 'hilfe/tickets/:slug', component: SupportTicketsPage },
  { path: 'soporte/tickets/:slug', component: SupportTicketsPage },
  { path: 'faq', component: FaqPage },
  { path: 'faq/:slug', component: FaqPage },
  { path: 'legal', component: LegalPage },
  { path: 'legal/:slug', component: LegalPage },
  { path: 'rechtliches', component: LegalPage },
  { path: 'rechtliches/:slug', component: LegalPage },
  { path: 'blog', component: BlogPage },
  { path: 'blog/:slug', component: BlogDetailPage },
  { path: 'arama', component: SearchPage },
  { path: 'search', component: SearchPage },
  { path: 'suche', component: SearchPage },
  { path: 'buscar', component: SearchPage },
  { path: 'recherche', component: SearchPage },
  { path: 'collection/:id/:slug', component: CollectionPage },
  { path: 'feed', component: FeedsPage },
  { path: 'feed/:id', component: FeedsPage },
  { path: 'live/:id', component: LivePage },
  { path: 'login', component: AuthPage },
  { path: 'register', component: AuthPage },
  { path: 'forgot-password', component: AuthPage },
];

const withPageData = (routes: Routes): Routes =>
  routes.map((route) => ({
    ...route,
    canActivate: [...(route.canActivate ?? []), siteRouteGuard, pageDataGuard],
  }));

// Workspace entry points must be checked before the locale catch-all route.
// Otherwise a direct `/seller-panel` load can be interpreted as a locale
// segment and render the localized home page instead of the seller workspace.
const workspaceRoutes: Routes = [
  { path: 'app/:widget', loadComponent: loadOperationalWorkspace },
  { path: 'app', loadComponent: loadOperationalWorkspace },
  { path: 'yonetim', loadComponent: loadQrMarketplaceDashboard },
  { path: 'product/:slug', component: ListingPage },
  { path: 'category/:slug', component: FortunePage },
  { path: 'seller-panel/free-fortunes', component: SellerFreeFortunesPage },
  { path: 'seller-panel/free-fortunes/:id', loadComponent: loadSellerFreeFortuneDetail },
  { path: 'seller-panel/ucretsiz-fallar', component: SellerFreeFortunesPage },
  { path: 'seller-panel/ucretsiz-fallar/:id', loadComponent: loadSellerFreeFortuneDetail },
  { path: 'seller-panel/orders/:id', loadComponent: () => import('./pages/seller-order-detail.page').then(m => m.SellerOrderDetailPage) },
  { path: 'seller-panel/siparisler/:id', loadComponent: () => import('./pages/seller-order-detail.page').then(m => m.SellerOrderDetailPage) },
  { path: 'seller-panel', loadComponent: loadSellerDashboard },
  { path: 'seller-panel/:section/:action/:id', loadComponent: loadSellerDashboard },
  { path: 'seller-panel/:section/:action', loadComponent: loadSellerDashboard },
  { path: 'seller-panel/:section', loadComponent: loadSellerDashboard },
  {
    path: 'admin-panel/urun-sablonlari/yeni',
    loadComponent: loadSellerDashboard,
    data: { productTemplateMode: true },
  },
  {
    path: 'admin-panel/product-templates/new',
    loadComponent: loadSellerDashboard,
    data: { productTemplateMode: true },
  },
  { path: 'admin-panel/system/theme', loadComponent: loadThemeEditor },
  { path: 'admin-panel/system/tema', loadComponent: loadThemeEditor },
  { path: 'admin-panel', loadComponent: loadAdminPanel },
  { path: 'admin-panel/:section/:action/:id', loadComponent: loadAdminPanel },
  { path: 'admin-panel/:section/:action', loadComponent: loadAdminPanel },
  { path: 'admin-panel/:section', loadComponent: loadAdminPanel },
];

export const routes: Routes = [
  ...withPageData([{ path: 'social-callback/facebook', component: FacebookSocialCallbackPage }, { path: 'social-callback/google-business', component: GoogleBusinessCallbackPage }, { path: 'social-callback/microsoft', component: MicrosoftCallbackPage }]),
  ...withPageData(workspaceRoutes),
  ...withPageData(pageRoutes),
  ...withPageData([{ path: '404', component: NotFoundPage }]),
  {
    path: ':branchSlug',
    canMatch: [qrMarketplaceBranchRouteGuard],
    loadComponent: loadQrBranchMenu,
    canActivate: [siteRouteGuard, pageDataGuard],
  },
  {
    path: ':slug',
    canMatch: [appointmentMarketplaceBranchRouteGuard],
    loadComponent: loadAppointmentBranchBooking,
    canActivate: [siteRouteGuard, pageDataGuard],
  },
  {
    path: ':locale',
    canMatch: [supportedLocaleRouteGuard],
    children: withPageData([...localizedPageRoutes, ...pageRoutes]),
  },
  ...withPageData([{ path: '**', component: NotFoundPage }]),
];
